import { Component } from '@theme/component';

/**
 * A horizontally scrolling row of category pills with overflow arrows.
 *
 * The scrolling itself is native (`overflow-x: auto` on the list), so the row stays usable
 * without JS. This only adds the arrow buttons and tracks how far the list is scrolled, exposing
 * `data-overflow-start` / `data-overflow-end` so the CSS can reveal the arrow for each direction
 * that has content left to reach.
 *
 * @extends {Component<{ scroller: HTMLElement }>}
 */
class CategoryScroller extends Component {
  requiredRefs = ['scroller'];

  #resizeObserver = new ResizeObserver(() => this.#update());

  connectedCallback() {
    super.connectedCallback();

    this.refs.scroller.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver.observe(this.refs.scroller);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.scroller.removeEventListener('scroll', this.#update);
    this.#resizeObserver.disconnect();
  }

  previous() {
    this.#scrollBy(-1);
  }

  next() {
    this.#scrollBy(1);
  }

  /**
   * Scrolls roughly one viewport of pills in the given direction.
   *
   * @param {number} direction - -1 to scroll towards the start, 1 towards the end.
   */
  #scrollBy(direction) {
    const { scroller } = this.refs;

    scroller.scrollBy({ left: direction * scroller.clientWidth * 0.8, behavior: 'smooth' });
  }

  #update = () => {
    const { scroller } = this.refs;
    // scrollLeft is negative in RTL, so compare against the travelled distance.
    const scrolled = Math.abs(scroller.scrollLeft);
    const scrollable = scroller.scrollWidth - scroller.clientWidth;

    this.toggleAttribute('data-overflow-start', scrolled > 1);
    this.toggleAttribute('data-overflow-end', scrolled < scrollable - 1);
  };
}

if (!customElements.get('category-scroller')) {
  customElements.define('category-scroller', CategoryScroller);
}
